import React, { useState } from 'react';
import { useAppDispatch, useAppSelector } from '@state/store';
import { setActiveScenario } from '@state/slices/studySlice';

export interface InvestigationPanelProps {
  isOpen: boolean;
  onToggle: () => void;
  selectedNodeId?: string;
  onSelectNode?: (nodeId: string) => void;
}

interface StructuralCategory {
  id: string;
  title: string;
  items: {
    id: string;
    label: string;
    sublabel?: string;
    badge?: string;
  }[];
}

export const InvestigationPanel: React.FC<InvestigationPanelProps> = ({
  isOpen,
  onToggle,
  selectedNodeId = 'study-overview',
  onSelectNode
}) => {
  const dispatch = useAppDispatch();
  const study = useAppSelector((state) => state.study.study);
  const activeScenarioId = study.activeScenarioId;
  const activeScenario = study.scenarios[activeScenarioId];

  const windSpeed = activeScenario?.cfdSetup.wind.speed ?? null;
  const domainWidth = activeScenario?.cfdSetup.domain.width ?? 100;
  const domainLength = activeScenario?.cfdSetup.domain.length ?? 100;
  const runCount = useAppSelector((state) => state.run.runOrder.length);
  const geometryCount = useAppSelector((state) => state.geometry.ids.length);

  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>({
    study: true,
    site: true,
    scenarios: true,
    runs: true,
    findings: false,
    assets: false
  });

  const categories: StructuralCategory[] = [
    {
      id: 'study',
      title: 'Study',
      items: [
        { id: 'study-overview', label: 'Study Overview', sublabel: 'Structural definition' }
      ]
    },
    {
      id: 'site',
      title: 'Site',
      items: [
        { id: 'site-terrain', label: 'Terrain' },
        { id: 'site-buildings', label: 'Geometry', badge: geometryCount > 0 ? `${geometryCount} ${geometryCount === 1 ? 'OBJECT' : 'OBJECTS'}` : undefined },
        { id: 'site-wind', label: 'Wind', badge: windSpeed !== null ? `${windSpeed.toFixed(0)}m/s` : 'UNSET' },
        { id: 'site-domain', label: 'Domain', badge: `${domainWidth}×${domainLength}m` },
        { id: 'site-boundaries', label: 'Boundaries' },
        { id: 'site-regions', label: 'Regions' }
      ]
    },
    {
      id: 'scenarios',
      title: 'Scenarios',
      items: Object.values(study.scenarios).map((sc) => ({
        id: sc.id,
        label: sc.name,
        badge: sc.id === activeScenarioId ? 'ACTIVE' : sc.isBaseline ? 'BASELINE' : 'VARIANT'
      }))
    },
    {
      id: 'runs',
      title: 'Runs',
      items: [
        { id: 'run-queue', label: 'Run Queue', badge: runCount > 0 ? `${runCount} RUNS` : 'READY' }
      ]
    },
    {
      id: 'findings',
      title: 'Findings',
      items: [
        { id: 'findings-summary', label: 'Analysis Summary', badge: 'EMPTY' }
      ]
    },
    {
      id: 'assets',
      title: 'Assets',
      items: [
        { id: 'assets-repository', label: 'Model Assets', badge: 'UNCONNECTED' }
      ]
    }
  ];

  const toggleSection = (sectionId: string) => {
    setExpandedSections((prev) => ({
      ...prev,
      [sectionId]: !prev[sectionId]
    }));
  };

  const handleItemClick = (itemId: string, categoryId: string) => {
    if (categoryId === 'scenarios') {
      dispatch(setActiveScenario(itemId));
      onSelectNode?.('study-overview');
    } else {
      onSelectNode?.(itemId);
    }
  };

  if (!isOpen) {
    return (
      <aside className="ssw-investigation-collapsed" aria-label="Investigation Panel">
        <button
          type="button"
          className="ssw-panel-expand-btn"
          onClick={onToggle}
          aria-label="Expand Investigation panel"
          title="Expand Investigation (Left Panel)"
        >
          <svg viewBox="0 0 16 16" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <path d="M4 3H12M4 8H12M4 13H8" />
          </svg>
          <span className="ssw-collapsed-panel-label">INVESTIGATION</span>
        </button>
      </aside>
    );
  }

  return (
    <aside className="ssw-investigation-panel" aria-label="Investigation Panel">
      {/* Panel Header */}
      <div className="ssw-panel-header">
        <div className="ssw-panel-title-lockup">
          <h2 className="ssw-panel-title">Investigation</h2>
        </div>
        <div className="ssw-panel-header-actions">
          {/* Collapse Panel Button */}
          <button
            type="button"
            className="ssw-panel-tool-btn"
            onClick={onToggle}
            aria-label="Collapse Investigation panel"
            title="Collapse panel"
          >
            <svg viewBox="0 0 16 16" width="14" height="14" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
              <path d="M10 3L5 8L10 13" />
            </svg>
          </button>
        </div>
      </div>

      {/* Panel Tree Body */}
      <div className="ssw-panel-body ssw-tree-container" role="tree" aria-label="Investigation Navigation Hierarchy">
        {categories.map((category) => {
          const isExpanded = !!expandedSections[category.id];
          return (
            <div key={category.id} className="ssw-tree-category" role="treeitem" aria-expanded={isExpanded}>
              <button
                type="button"
                className="ssw-tree-category-header"
                onClick={() => toggleSection(category.id)}
              >
                <svg
                  className={`ssw-tree-chevron ${isExpanded ? 'is-open' : ''}`}
                  viewBox="0 0 12 12"
                  width="10"
                  height="10"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  aria-hidden="true"
                >
                  <path d="M4 2.5L7.5 6L4 9.5" />
                </svg>
                <span className="ssw-tree-category-title">{category.title}</span>
              </button>

              {isExpanded && (
                <div className="ssw-tree-node-list" role="group">
                  {category.items.map((item) => {
                    const isSelected = selectedNodeId === item.id || (category.id === 'scenarios' && item.id === activeScenarioId && selectedNodeId === 'study-overview');
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="treeitem"
                        aria-selected={isSelected}
                        className={`ssw-tree-node-item ${isSelected ? 'is-selected' : ''}`}
                        onClick={() => handleItemClick(item.id, category.id)}
                      >
                        <span className="ssw-tree-node-pip" aria-hidden="true" />
                        <span className="ssw-tree-node-label">{item.label}</span>
                        {item.badge && (
                          <span
                            className="ssw-tree-node-badge"
                            style={{
                              background: item.badge === 'ACTIVE' ? 'rgba(34, 197, 94, 0.12)' : undefined,
                              color: item.badge === 'ACTIVE' ? '#15803d' : undefined,
                              fontWeight: item.badge === 'ACTIVE' ? 700 : undefined
                            }}
                          >
                            {item.badge}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </aside>
  );
};

export default InvestigationPanel;
